import * as PrefabManager from '../PrefabManager';
import { Explosive } from './Explosive';
import * as AudioManager from '../../utils/AudioManager';
import * as PhysicsParticleEmitter from '../../utils/PhysicsParticleEmitter';

import {
    game
} from "../../Game";

// Index of the knob in the mine's vertices; it is listed first so the collar draws over its foot.
const KNOB = 0;
const FUSE_BEEP = 150;

export class Landmine extends Explosive {
    static triggeredByTypes = {
        anything: 'Anything',
        anyCharacter: 'Any Character',
        mainCharacter: 'Main Character',
    };

    init() {
        super.init();
        this.explosiveRadius = 400;
        this.explosionScale = 0.7;
        this.mine = this.lookupObject.mine;
        this.explodeTarget = this.mine;
        this.triggeredBy = this.prefabObject.settings.triggeredBy;
        this.explodeOnRelease = this.prefabObject.settings.explodeOnRelease;
        this.pressingContacts = new Set();
        this.clicked = false;
        this.fuse = -1;
    }

    counts(body) {
        if (body === this.mine) return false;
        switch (this.triggeredBy) {
            case Landmine.triggeredByTypes.mainCharacter:
                return Boolean(body.mainCharacter);
            case Landmine.triggeredByTypes.anyCharacter:
                return Boolean(body.isHumanoid || body.mainCharacter);
            default:
                return true;
        }
    }

    knobContact(contact) {
        const fixtureA = contact.GetFixtureA();
        const fixtureB = contact.GetFixtureB();
        let other;
        if (fixtureA.GetBody() === this.mine && fixtureA.verticeRef === KNOB) other = fixtureB;
        else if (fixtureB.GetBody() === this.mine && fixtureB.verticeRef === KNOB) other = fixtureA;
        if (!other || other.IsSensor()) return false;
        return this.counts(other.GetBody());
    }

    pressKnob(contact) {
        if (!this.active || this.exploded || this.explosionQueued || this.destroyed) return;
        this.pressingContacts.add(Box2D.getPointer(contact));
        if (this.clicked) return;
        this.clicked = true;
        AudioManager.playSFX('grenade-pin', 0.3, 0.8, this.mine.GetPosition());
        if (!this.explodeOnRelease) this.lightFuse();
    }

    releaseKnob(contact) {
        if (!this.pressingContacts.delete(Box2D.getPointer(contact))) return;
        if (this.explodeOnRelease && this.clicked && this.fuse < 0 && this.pressingContacts.size === 0) this.lightFuse();
    }

    lightFuse() {
        if (this.explodeDelay <= 0) {
            this.explode();
            return;
        }
        this.fuse = this.explodeDelay;
        this.beepTime = 0;
    }

    explode() {
        if (this.exploded || this.explosionQueued) return;
        this.explodeTarget = this.mine;
        super.explode();
    }

    performExplosion() {
        if (this.exploded || this.destroyed) return;
        super.performExplosion();

        const pos = this.mine.GetPosition();
        if (!this.denseExplosion) {
            PhysicsParticleEmitter.emit(['Cardboard_1', 'Cardboard_2', 'Cardboard_3'], pos, 8, 4, 18, false, [0x1f4f19, 0x515151]);
        }
        AudioManager.playSFX('barrel-explosion', 0.3, 1.15 + 0.3 * Math.random() - 0.15, pos);

        this.destroy();
    }

    update() {
        if (this.destroyed || this.fuse < 0) return;
        const deltaTime = game.editor.deltaTime;
        this.fuse -= deltaTime;
        this.beepTime -= deltaTime;
        if (this.beepTime <= 0) {
            this.beepTime = FUSE_BEEP;
            AudioManager.playSFX('drone-beep', 0.1, 1.3, this.mine.GetPosition());
        }
        if (this.fuse <= 0) {
            this.fuse = -1;
            this.explode();
        }
    }

    set(property, value) {
        this.prefabObject.settings[property] = value;
        if (property !== 'active') return;
        this.active = value;
        if (!value) {
            // Disarming defuses a mine that was already stepped on.
            this.clicked = false;
            this.fuse = -1;
            this.pressingContacts?.clear();
        }
    }

    initContactListener() {
        super.initContactListener();
        const self = this;
        this.contactListener.BeginContact = function (contact) {
            if (self.knobContact(contact)) self.pressKnob(contact);
        };
        this.contactListener.EndContact = function (contact) {
            if (self.knobContact(contact)) self.releaseKnob(contact);
        };
        this.contactListener.PostSolve = function () {};
    }
}

Landmine.settings = Object.assign({}, Explosive.settings, {
    "delay": 0,
    "force": 4000,
    "active": true,
    "triggeredBy": Landmine.triggeredByTypes.anything,
    "explodeOnRelease": false,
});
delete Landmine.settings.activateOn;

Landmine.settingsOptions = Object.assign({}, Explosive.settingsOptions, {
    "delay": {
        min: 0.0,
        max: 5.0,
        step: 0.05
    },
    "triggeredBy": Object.values(Landmine.triggeredByTypes),
    "explodeOnRelease": false,
});
delete Landmine.settingsOptions.activateOn;

const knob = [{ x: -0.18, y: -0.19 }, { x: -0.18, y: -0.32 }, { x: 0.18, y: -0.32 }, { x: 0.18, y: -0.19 }];
const collar = [{ x: -0.3, y: -0.12 }, { x: -0.3, y: -0.22 }, { x: 0.3, y: -0.22 }, { x: 0.3, y: -0.12 }];
const body = [{ x: -1.2, y: 0.15 }, { x: -1.05, y: -0.15 }, { x: 1.05, y: -0.15 }, { x: 1.2, y: 0.15 }];

PrefabManager.prefabLibrary.Landmine = {
    json: JSON.stringify({ objects: [[0, 0, 0, 0, "landmine", "mine", 0,
        ["#8a8a8a", "#515151", "#1f4f19"], ["#000", "#000", "#000"], [1, 1, 1], false, true,
        [knob, collar, body], [4, 4, 4], 0, [0, 0, 0], "", [0.8, 0.8, 0.8], true, false, false, [0.8, 0.8, 0.8], [0, 0, 0]]] }),
    class: Landmine,
    library: PrefabManager.LIBRARY_WEAPON,
}
